<script setup lang="ts">
import { ref } from 'vue';
import { careerMetadata } from '@/utils/career-metadata';
import { type GroupedPatientRow, patientRequirementsPath } from './grouping';

// Phone rendering of a monthly-tracking row: tap the header to expand the
// per-career breakdown (the desktop table's #expanded-row).
interface Props {
  row: GroupedPatientRow;
  linkToPatient?: boolean;
}

defineProps<Props>();

const PERCENT_MULTIPLIER = 100;
const WARNING_THRESHOLD = 0.8;
const OVER_THRESHOLD = 1.0;
const HIGH_DRIFT = 3;

const expanded = ref(false);

function utilizationColor(percent: number): string {
  if (percent > OVER_THRESHOLD) return 'error';
  if (percent >= WARNING_THRESHOLD) return 'warning';
  if (percent > 0) return 'success';
  return 'grey';
}

function driftColor(count: number): string {
  if (count === 0) return 'success';
  if (count < HIGH_DRIFT) return 'warning';
  return 'error';
}

function formatPercent(percent: number): string {
  return `${Math.round(percent * PERCENT_MULTIPLIER)}%`;
}
</script>
<template>
  <v-card
    variant="outlined"
    class="pa-3"
  >
    <div
      class="d-flex align-center ga-2"
      @click="expanded = !expanded"
    >
      <span class="font-weight-bold text-body-1 text-truncate flex-grow-1">
        <a
          v-if="linkToPatient"
          :href="patientRequirementsPath(row.patientId)"
          class="patient-name-link"
          @click.stop
        >{{ row.patientFullName || '—' }}</a>
        <template v-else>{{ row.patientFullName || '—' }}</template>
      </span>
      <v-chip
        :color="utilizationColor(row.percentUsed)"
        size="small"
        variant="tonal"
        class="flex-shrink-0"
      >
        {{ formatPercent(row.percentUsed) }}
      </v-chip>
      <v-icon :icon="expanded ? 'mdi-chevron-up' : 'mdi-chevron-down'" />
    </div>
    <div class="d-flex flex-wrap align-center ga-3 mt-2 text-body-2">
      <span>
        <span class="text-medium-emphasis">Presup.:</span> {{ row.cap }}
      </span>
      <span>
        <span class="text-medium-emphasis">Agend.:</span> {{ row.planned }}
      </span>
      <span>
        <span class="text-medium-emphasis">Realiz.:</span> {{ row.actual }}
      </span>
      <v-chip
        :color="driftColor(row.driftCount)"
        size="x-small"
        variant="tonal"
      >
        {{ row.driftCount === 0 ? 'Sin novedad' : `${row.driftCount} día(s)` }}
      </v-chip>
    </div>
    <div
      v-if="expanded"
      class="mt-3"
    >
      <v-divider class="mb-2" />
      <div
        v-for="child in row.children"
        :key="child.career"
        class="d-flex align-center ga-2 py-1"
      >
        <v-avatar
          :color="careerMetadata(child.career).color"
          size="24"
          variant="tonal"
        >
          <v-icon
            :icon="careerMetadata(child.career).icon"
            size="14"
          />
        </v-avatar>
        <span class="text-body-2 flex-grow-1 text-truncate">{{ child.humanCareer }}</span>
        <span class="text-caption text-medium-emphasis">{{ child.actual }}/{{ child.cap }}</span>
        <span
          class="text-caption font-weight-medium"
          :class="`text-${utilizationColor(child.percentUsed)}`"
        >
          {{ formatPercent(child.percentUsed) }}
        </span>
      </div>
    </div>
  </v-card>
</template>

<style scoped>
/* Tapping the name opens the patient; it keeps looking like plain text. */
.patient-name-link {
  color: inherit;
  text-decoration: none;
}
</style>
